import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSelectedImage } from "@insite/client-framework/Store/Pages/ProductDetails/ProductDetailsSelectors";
import translate from "@insite/client-framework/Translate";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import Button, { ButtonIcon, ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ProductPriceStyles } from "@tCentro/Components/CENCustomProductPrice";
import CENProductPriceCustomPDP, { ProductPriceStylesPDP } from "@tCentro/Components/CENProductPriceCustomPDP";
import cenQtyMin from "@tCentro/Handlers/CenQtyMinHandler";
import cenQtyMulti from "@tCentro/Handlers/CenQtyMultiHandler";
import CENErrorIcon from "@tCentro/Icons/CENErrorIcon";
import ProductAddToCartButton from "@tCentro/Overrides/Components/ProductAddToCartButton";
import { InfoIconTooltipStyles } from "@tCentro/Overrides/Components/ProductAddToCartButton";
import ProductPrimaryImage, { ProductPrimaryImageStyles } from "@tCentro/Overrides/Components/ProductPrimaryImage";
import ProductQuantityOrdered from "@tCentro/Overrides/Components/ProductQuantityOrdered";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState & CustomApplicationState, ownProps: HasProduct) => {
    const { productInfo, product } = ownProps;
    return {
        selectedImage: getSelectedImage(state, product),
        configurationSelection: state.pages.productDetails.configurationSelection,
        configurationCompleted: state.pages.productDetails.configurationCompleted,
        variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
        qtyOrdered: productInfo.qtyOrdered,
        isVariantParent: product.isVariantParent,
        productSettings: getSettingsCollection(state).productSettings,
        productContext: ownProps,
        subscription: state.pages.productDetails.subscription,
        mutipleSaleQty: product?.detail?.multipleSaleQty,
        roundingRule: product?.detail?.roundingRule,
        isMinErrorObj: state.t_components.isMinError,
        isMultiErrorObj: state.t_components.isMultiError,
    };
};

const mapDispatchToProps = {
    cenQtyMin,
    cenQtyMulti,
};

export interface ScrollingBarStyles {
    tooltipContainerStyles?: InjectableCss;
    errorMsgStyle?: InjectableCss;
    errorIconStyles?: IconPresentationProps;
    errorMsgStyleTypo?: TypographyPresentationProps;
    mainContainer?: GridContainerProps;
    productImageWrapper?: GridItemProps;
    productPriceWrapper?: GridItemProps;
    productQTyWrapper?: GridItemProps;
    productAddtoCartWrapper?: GridItemProps;
    productTitleWrapper?: GridItemProps;
    primaryImage?: ProductPrimaryImageStyles;
    brandGridItem?: InjectableCss;
    brandStyles?: ProductBrandStyles;
    titleGridItem?: InjectableCss;
    titleText?: TypographyProps;
    labelText?: TypographyProps;
    labelValue?: TypographyProps;
    valueText?: TypographyProps;
    productPrice?: ProductPriceStylesPDP;
    quantityOrderedWrapper?: InjectableCss;
    addQTYBtn?: ButtonProps;
    subQTYBtn?: ButtonProps;
    quantityOrdered?: TextFieldProps;
    button?: ButtonPresentationProps & InfoIconTooltipStyles;
    leftPanelRow?: GridItemProps;
    rightPanelRow?: GridItemProps;
    hideMainContainer?: GridContainerProps;
}

export const scrollingBarStyles: ScrollingBarStyles = {
    errorMsgStyleTypo: {
        color: "danger",
        css: css`
            font-weight: 400;
            font-size: 14px;
            line-height: 20px;
        `,
    },
    errorMsgStyle: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 4px;
            position: absolute;
            margin-top: 4px;
            bottom: 30px;
            margin-left: 8px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css``,
                    css``,
                    css`
                        bottom: 2px;
                    `,
                    null,
                    null,
                ])}
            @media (min-width: 1024px) and (max-width: 1279px) {
                bottom: 8px;
            }
        `,
    },
    tooltipContainerStyles: {
        css: css`
            background-color: #262525;
            border-radius: 4px;
            width: max-content;
            border-radius: 4px;
            box-shadow: 0px 4px 8px 0px rgba(38, 37, 37, 0.24);
            position: absolute;
            top: -29px;
            right: 30px;
            z-index: 999;
            font-size: 12px;
            font-style: normal;
            font-weight: 600;
            line-height: 16px;
            padding: 8px 16px;
            color: #262525;
            @media (max-width: 1024px) {
                top: -26px;
                right: 40px;
            }
            &:after {
                content: "";
                border-left: 0px solid ${getProp("theme.typography.p.color")};
                border-right: 4px solid ${getProp("theme.typography.p.color")};
                position: absolute;
                z-index: 999;
                top: 29px;
                left: 42%;
                width: 0px;
                height: 16px;
                width: 0px;
                height: 0px;
                border-left: 10px solid transparent;
                border-right: 10px solid transparent;
                border-top: 16px solid ${getProp("theme.typography.p.color")};
            }
            @media (min-width: 1024px) and (max-width: 1279px) {
                right: 50px;
            }
        `,
    },
    mainContainer: {
        css: css`
            margin: 0 auto;
            width: 100%;
            position: fixed;
            top: 0px;
            left: 0px;
            right: 0px;
            z-index: 9999;
            background: #fff;
            padding: 20px 20px 15px 50px;
            box-shadow: 0px 4px 8px 0px #2625253d;
            transition: all 0.5s ease-in-out;

            opacity: 1;
            transform: translateY(0);

            &.hideBar {
                opacity: 0;
                transform: translateY(-100%);
                z-index: -1;
            }

            > div[class^="GridOffset"] {
                display: flex;
                flex-wrap: wrap;
                width: 100%;
                justify-content: center;
                align-items: center;
            }

            @media (min-width: 992px) and (max-width: 1199px) {
                padding: 20px 20px 15px 35px;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        padding: 20px 20px 15px 20px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    hideMainContainer: {
        css: css`
            transition: all 1s;
            z-index: -1;
            opacity: 0;
        `,
    },
    productImageWrapper: {
        width: [2, 2, 3, 3, 2],
        css: css`
            ${({ theme }: { theme: BaseTheme }) => breakpointMediaQueries(theme, [null, null, null, null, null])}
        `,
    },
    productTitleWrapper: {
        width: [6, 6, 9, 9, 10],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) => breakpointMediaQueries(theme, [null, null, null, null, null])}
        `,
    },
    productPriceWrapper: {
        width: [6, 6, 12, 4, 4],
        css: css`
            align-items: center;
            padding-top: 0px;
            position: relative;
            top: -10px;
            @media (min-width: 992px) and (max-width: 1199px) {
                div {
                    justify-content: end;
                }
                span {
                    text-align: right;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                        width: 100%;
                        align-items: flex-end;
                        justify-content: end;
                        padding-left: 0px;
                        padding-right: 0px;
                        div {
                            justify-content: end;
                        }
                        span {
                            text-align: right;
                        }
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                        width: 100%;
                        align-items: flex-end;
                        justify-content: end;
                    `,
                    null,
                ])}
        `,
    },
    productQTyWrapper: {
        width: [6, 6, 6, 4, 4],
        css: css`
            align-items: center;
            padding-top: 0px;
            @media (min-width: 992px) and (max-width: 1199px) {
                flex-basis: 50%;
                max-width: 50%;
            }
            @media (min-width: 1024px) and (max-width: 1279px) {
                padding-bottom: 40px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        flex-basis: 50%;
                        max-width: 50%;
                        padding-right: 0px;
                        padding-bottom: 40px !important;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    productAddtoCartWrapper: {
        width: [6, 6, 6, 4, 4],
        css: css`
            justify-content: center;
            align-items: center;
            padding-top: 0px;
            @media (min-width: 992px) and (max-width: 1199px) {
                flex-basis: 50%;
                max-width: 50%;
            }
            @media (min-width: 1024px) and (max-width: 1279px) {
                padding-bottom: 40px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        flex-basis: 50%;
                        max-width: 50%;
                        padding-bottom: 40px !important;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    primaryImage: {
        centeringWrapper: {
            css: css`
                min-height: 130px !important;
                padding: 0px !important;
                img {
                    max-height: 250px;
                    width: 110px !important;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                            img {
                                width: 70px !important;
                            }
                        `,
                        null,
                        null,
                    ])}
            `,
        },
        productImageWrapper: {
            css: css`
                height: 130px !important;
                width: 100% !important;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                            height: 100px !important;
                        `,
                        null,
                        null,
                    ])}
            `,
        },
        productDisclaimerMsg: {
            css: css`
                display: none;
            `,
        },
    },
    brandStyles: {
        logoImage: {
            css: css`
                img {
                    max-width: 150px;
                    max-height: 150px;
                }
            `,
        },
        nameText: {
            size: 16,
            weight: "normal",
        },
        nameLink: {
            css: css`
                > span {
                    font-size: ${TYPOGRAPHY.size.smaller};
                    line-height: 16px;
                }
            `,
        },
    },
    titleText: {
        css: css`
            font-family: Poppins;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: ${TYPOGRAPHY.size.large};
            line-height: 32px;
            color: #262525;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        font-size: ${TYPOGRAPHY.size.smaller};
                        line-height: normal;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    labelText: {
        css: css`
            margin-right: 4px;
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 24px;
            color: ${getProp("theme.typography.p.color")};
        `,
    },
    labelValue: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.base};
            line-height: 24px;
            color: ${getProp("theme.typography.p.color")};
        `,
    },
    valueText: {
        size: 14,
        css: css`
            margin-bottom: 5px;
        `,
    },
    productPrice: {
        price: {
            priceText: { size: 26 },
            unitOfMeasureText: { size: 26 },
        },
        requireDrawingApprovalWrapper: {
            css: css`
                padding: 0px;
            `,
        },
    },
    quantityOrderedWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            > [class*="FormFieldStyle-"] {
                label {
                    display: none;
                }
            }
        `,
    },
    addQTYBtn: {
        css: css`
            height: 40px;
            margin-top: 0px;
            background-color: ${COLORS.white};
            padding: 0px;
            position: absolute;
            right: 20px;
            z-index: 1;
            border: none;
            & svg {
                color: ${COLORS.black};
            }
            &:hover {
                background-color: ${COLORS.white};
                border: none;
            }
            &:focus {
                outline: none;
            }
            &:disabled,
            &:disabled:hover {
                background: transparent;
            }
        `,
    },
    subQTYBtn: {
        css: css`
            height: 40px;
            margin-top: 0px;
            background-color: transparent;
            padding: 0px;
            position: absolute;
            left: 20px;
            z-index: 1;
            border: none;
            & svg {
                color: ${COLORS.black};
            }
            &:hover {
                background-color: transparent;
                border: none;
            }
            &:focus {
                outline: none;
            }
            &:disabled,
            &:disabled:hover {
                background: transparent;
            }
        `,
    },
    quantityOrdered: {
        cssOverrides: {
            formField: css`
                margin-top: 0px;
                width: initial;
                flex-direction: row !important;
                align-items: center;

                label {
                    font-weight: ${TYPOGRAPHY.weight.mediumbold};
                    font-size: ${TYPOGRAPHY.size.base};
                    line-height: 24px;
                    width: 35% !important;
                }

                > div {
                    margin-top: 0 !important;

                    input {
                        padding: 12px 18px 12px 24px !important;
                        height: 48px !important;
                        width: 130px !important;
                        border: 1px solid #262525;
                        text-align: center;
                    }
                }

                @media (min-width: 992px) and (max-width: 1180px) {
                    > div {
                        input {
                            width: 150px !important;
                        }
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                            > div {
                                input {
                                    width: 140px !important;
                                }
                            }
                        `,
                        null,
                        null,
                    ])}
            `,
        },
    },
    button: {
        css: css`
            width: 100%;
            border-radius: 24px !important;
            width: auto !important;
            height: 48px !important;
            white-space: nowrap;
            display: flex;
            flex-direction: column;
            justify-content: center;
            &:disabled {
                background: ${getColor("secondary")};
                border-color: ${getColor("secondary")};
                opacity: 0.5;
            }
            > span {
                font-size: ${TYPOGRAPHY.size.large};
                font-family: ${TYPOGRAPHY.fontFamily.base};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                text-align: center;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            font-size: ${TYPOGRAPHY.size.smaller};
                        `,
                        null,
                        null,
                        null,
                        null,
                    ])}
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: max-content !important;
                        `,
                        css`
                            margin-top: 16px;
                        `,
                        css`
                            width: 150px !important;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,

        hiddenMobileToolTip: {
            css: css`
                display: inline;
                position: relative;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            left: 35px;
                            top: 12px;
                            position: absolute;
                        `,
                        css`
                            left: 35px;
                            top: 28px;
                            position: absolute;
                        `,
                        css`
                            left: 4%;
                            top: 12px;
                            position: absolute;
                        `,
                        css`
                            @media (min-width: 992px) and (max-width: 1024px) {
                                top: 12px;
                                left: 10%;
                                position: absolute;
                            }
                        `,
                        null,
                    ])}
            `,
        },
        mobileTooltip: {
            cssOverrides: {
                tooltipBody: css`
                    left: 0px;
                `,
            },
        },
        tooltipButtonContainer: {
            css: css`
                display: flex;
                position: relative;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            flex-direction: column-reverse;
                            align-items: flex-start !important;
                            margin-top: 0px;
                        `,
                        css`
                            flex-direction: column-reverse;
                            align-items: flex-start !important;
                            margin-top: 0px;
                        `,
                        css`
                            flex-direction: column-reverse;
                            align-items: flex-start !important;
                            margin-left: 10px;
                        `,
                        null,
                        null,
                    ])}
            `,
        },
    },
    leftPanelRow: {
        width: [6, 6, 7, 6, 7],
        css: css`
            gap: 30px;
            padding: 0px;
            @media (min-width: 992px) and (max-width: 1199px) {
                flex-basis: 60.333333%;
                max-width: 60.333333%;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        flex-basis: 58.333333%;
                        max-width: 58.333333%;
                        gap: 10px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    rightPanelRow: {
        width: [6, 6, 5, 6, 5],
        css: css`
            gap: 30px;
            padding: 0px;
            align-items: center;
            @media (min-width: 992px) and (max-width: 1199px) {
                gap: 0px;
                flex-wrap: wrap;
                flex-basis: 39.666667%;
                max-width: 39.666667%;
                padding-top: 15px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        gap: 0px;
                        flex-wrap: wrap;
                        flex-basis: 41.666667%;
                        max-width: 41.666667%;
                        padding-top: 15px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = scrollingBarStyles;

const CENPdpscrollingHeader = ({
    productContext,
    selectedImage,
    productSettings,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    isVariantParent,
    qtyOrdered,
    subscription,
    mutipleSaleQty,
    roundingRule,
    isMinErrorObj,
    isMultiErrorObj,
    cenQtyMin,
    cenQtyMulti,
}: Props) => {
    const isMinError = isMinErrorObj?.isMinError;
    const isMultiError = isMultiErrorObj?.isMultiError;
    const { productInfo, product } = productContext;
    if (!product) {
        return null;
    }
    const [btnQty, setBtnQty] = React.useState(qtyOrdered);
    useEffect(() => {
        cenQtyMin({ isMinError: false });
        cenQtyMulti({ isMultiError: false });
    }, []);

    const [isFixedBottom, setIsFixedBottom] = React.useState(false);
    const qtyRef = React.useRef<HTMLDivElement | null>(null);
    const [showTooltip, setShowTooltip] = React.useState(false);
    const handleMouseEnter = () => {
        if (mutipleSaleQty && mutipleSaleQty > 0 && roundingRule === "Multiple Only" && qtyOrdered === mutipleSaleQty) {
            setShowTooltip(true);
        } else {
            setShowTooltip(false);
        }
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
    };
    React.useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        const isLaptopDesktop = () => window.innerWidth >= 768;
        const headerElement = document.querySelector("header") as HTMLHeadElement | null;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (isLaptopDesktop()) {
                    setIsFixedBottom(!entry.isIntersecting);
                    if (headerElement) {
                        headerElement.style.display = !entry.isIntersecting ? "none" : "block";
                    }
                }
            },
            { threshold: 0 },
        );

        // Observe the correct section based on current width
        const observeCorrectSection = () => {
            const newTarget =
                window.innerWidth >= 768 && window.innerWidth <= 991
                    ? (document.querySelector(".mobileTabletSection") as HTMLDivElement)
                    : (document.querySelector(".desktopSection") as HTMLDivElement);

            if (newTarget && qtyRef.current !== newTarget) {
                if (qtyRef.current) {
                    observer.unobserve(qtyRef.current);
                }
                qtyRef.current = newTarget;
                observer.observe(newTarget);
            }
        };

        observeCorrectSection(); // initial observation

        window.addEventListener("resize", observeCorrectSection); // re-evaluate on resize

        return () => {
            window.removeEventListener("resize", observeCorrectSection);
            if (qtyRef.current) {
                observer.unobserve(qtyRef.current);
            }
            setIsFixedBottom(false);
            if (headerElement) {
                headerElement.style.display = "";
            }
        };
    }, []);

    const addQtyFn = () => {
        setBtnQty(
            qtyOrdered +
                (mutipleSaleQty && mutipleSaleQty > 0 && roundingRule === "Multiple Only" ? mutipleSaleQty : 1),
        );
    };

    const subQtyFn = () => {
        const qtyValue =
            qtyOrdered -
            (mutipleSaleQty && mutipleSaleQty > 0 && roundingRule === "Multiple Only" ? mutipleSaleQty : 1);
        if (qtyValue === 0 || Math.sign(qtyValue) === -1) {
            setBtnQty(0);
            return false;
        }
        setBtnQty(qtyValue);
    };
    const image =
        selectedImage ??
        ({
            id: product.id,
            imageAltText: product.imageAltText,
            imageType: "Static",
            largeImagePath: product.largeImagePath,
            mediumImagePath: product.mediumImagePath,
            smallImagePath: product.smallImagePath,
        } as ImageModel);
    return (
        <GridContainer
            {...styles.mainContainer}
            className={isFixedBottom ? "" : "hideBar"}
            data-test-selector="productDetails_scrollingbar"
        >
            <GridItem {...styles.leftPanelRow}>
                <GridItem {...styles.productImageWrapper}>
                    <ProductPrimaryImage product={product} image={image} extendedStyles={styles.primaryImage} />
                </GridItem>

                <GridItem {...styles.productTitleWrapper}>
                    {product.brand && (
                        <StyledWrapper {...styles.brandGridItem}>
                            <ProductBrand brand={product.brand} showLogo={false} extendedStyles={styles.brandStyles} />
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.titleGridItem}>
                        <Typography
                            {...styles.titleText}
                            data-test-selector="productDetails_scrollingheader_productDescription"
                        >
                            {product.productTitle}
                        </Typography>

                        {product?.manufacturerItem && (
                            <Typography {...styles.valueText} as="p">
                                <Typography {...styles.labelText}>{translate("MPN:")}</Typography>
                                <Typography {...styles.labelValue}> {product.manufacturerItem}</Typography>
                            </Typography>
                        )}

                        {product?.productNumber && (
                            <Typography {...styles.valueText} as="p" data-test-selector="ProductDetailsPartNumber">
                                <Typography {...styles.labelText}>{translate("Part #")}</Typography>
                                <Typography {...styles.labelValue}> {product.productNumber}</Typography>
                            </Typography>
                        )}
                    </StyledWrapper>
                </GridItem>
            </GridItem>

            <GridItem {...styles.rightPanelRow}>
                <GridItem {...styles.productPriceWrapper}>
                    {productContext?.product?.isVariantParent ? (
                        <>
                            <Typography
                                as="p"
                                css={css`
                                    margin: 18px 0px 18px 6px;
                                    color: ${COLORS.text.mineShaft};
                                    font-family: ${TYPOGRAPHY.fontFamily.title};
                                    font-size: ${TYPOGRAPHY.size.large};
                                    font-weight: ${TYPOGRAPHY.weight.mediumbold};
                                    line-height: ${TYPOGRAPHY.lineHeight.base};
                                    text-align: left;
                                `}
                            >
                                {siteMessage("Parent_Variant_PricingMessage")}
                            </Typography>
                        </>
                    ) : (
                        <CENProductPriceCustomPDP
                            product={productContext}
                            showLabel={false}
                            showSavings={true}
                            showSavingsAmount={productSettings.showSavingsAmount}
                            showSavingsPercent={productSettings.showSavingsPercent}
                            extendedStyles={styles.productPrice}
                        />
                    )}
                </GridItem>

                <GridItem {...styles.productQTyWrapper}>
                    <StyledWrapper {...styles.quantityOrderedWrapper}>
                        <Button
                            disabled={
                                !!(
                                    (!variantSelectionCompleted && isVariantParent) ||
                                    (mutipleSaleQty &&
                                        mutipleSaleQty > 0 &&
                                        roundingRule === "Multiple Only" &&
                                        qtyOrdered === mutipleSaleQty)
                                )
                            }
                            {...styles.subQTYBtn}
                            data-test-selector="subQTYBtn"
                            onClick={subQtyFn}
                            onMouseOver={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                        >
                            <ButtonIcon src={Minus} />
                        </Button>
                        {showTooltip && (
                            <StyledWrapper {...styles.tooltipContainerStyles}>
                                <Typography
                                    as="span"
                                    css={css`
                                        color: ${getColor("info.contrast")} !important;
                                    `}
                                >
                                    {translate(`${mutipleSaleQty} unit minimum`)}
                                </Typography>
                            </StyledWrapper>
                        )}
                        <ProductQuantityOrdered
                            configurationSelection={configurationSelection}
                            configurationCompleted={configurationCompleted}
                            variantSelectionCompleted={variantSelectionCompleted}
                            extendedStyles={styles.quantityOrdered}
                            onBtnclickQtyValue={btnQty}
                            setBtnQty={setBtnQty}
                        />
                        <Button
                            disabled={!variantSelectionCompleted && isVariantParent}
                            {...styles.addQTYBtn}
                            data-test-selector="addQTYBtn"
                            onClick={addQtyFn}
                        >
                            <ButtonIcon src={Plus} />
                        </Button>
                    </StyledWrapper>
                    {isMultiError === true && (
                        <StyledWrapper {...styles.errorMsgStyle}>
                            <Icon {...styles.errorIconStyles} src={CENErrorIcon} />
                            <Typography {...styles.errorMsgStyleTypo}>
                                {translate(`This product must be ordered in multiples of ${mutipleSaleQty}`)}
                            </Typography>
                        </StyledWrapper>
                    )}
                    {isMinError === true && (
                        <StyledWrapper {...styles.errorMsgStyle}>
                            <Icon {...styles.errorIconStyles} src={CENErrorIcon} />
                            <Typography {...styles.errorMsgStyleTypo}>
                                {translate(`This product has a minimum order of ${mutipleSaleQty} units`)}
                            </Typography>
                        </StyledWrapper>
                    )}
                </GridItem>

                <GridItem {...styles.productAddtoCartWrapper}>
                    <ProductAddToCartButton
                        configurationSelection={configurationSelection}
                        configurationCompleted={configurationCompleted}
                        variantSelectionCompleted={variantSelectionCompleted}
                        subscription={subscription}
                        extendedStyles={styles.button}
                        data-test-selector="addProductToCart"
                    />
                </GridItem>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps, mapDispatchToProps)(CENPdpscrollingHeader)),
    definition: {
        displayName: "CEN Product Detail Scrolling bar",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
